import { ArrowRight, UserCheck, User, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SignupForm } from '@/lib/signup.constants';
import { AUTH_FIELD_CLASS, AUTH_PRIMARY_BUTTON_CLASS } from '@/features/auth-shell';
import type { translations } from '@/translations/translations';

type TranslationKeys = typeof translations.fr;

interface SignupStep1Props {
  form: Pick<SignupForm, 'username' | 'name' | 'email'>;
  onUpdate: (fields: Partial<SignupForm>) => void;
  t: TranslationKeys;
}

const ICON_CLASS = 'pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground';

export const SignupStep1 = ({ form, onUpdate, t }: SignupStep1Props) => (
  <div className="space-y-5" data-testid="signup-step-1">
    <div className="space-y-2">
      <Label htmlFor="username" className="text-sm font-medium text-foreground">
        {t.username} <span className="text-brand" aria-hidden="true">*</span>
      </Label>
      <div className="relative">
        <UserCheck className={ICON_CLASS} strokeWidth={1.75} />
        <Input
          id="username"
          type="text"
          autoComplete="username"
          placeholder={t.username}
          value={form.username}
          onChange={(e) => onUpdate({ username: e.target.value })}
          required
          className={`pl-10 ${AUTH_FIELD_CLASS}`}
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="name" className="text-sm font-medium text-foreground">
        {t.fullName} <span className="text-brand" aria-hidden="true">*</span>
      </Label>
      <div className="relative">
        <User className={ICON_CLASS} strokeWidth={1.75} />
        <Input
          id="name"
          type="text"
          autoComplete="name"
          placeholder={t.fullName}
          value={form.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          required
          className={`pl-10 ${AUTH_FIELD_CLASS}`}
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="email" className="text-sm font-medium text-foreground">
        {t.emailAddress} <span className="font-normal text-faint-foreground">({t.optionalLabel})</span>
      </Label>
      <div className="relative">
        <Mail className={ICON_CLASS} strokeWidth={1.75} />
        <Input
          id="email"
          type="email"
          autoComplete="email"
          placeholder={t.emailPlaceholder}
          value={form.email}
          onChange={(e) => onUpdate({ email: e.target.value })}
          className={`pl-10 ${AUTH_FIELD_CLASS}`}
        />
      </div>
    </div>

    <Button type="submit" className={AUTH_PRIMARY_BUTTON_CLASS}>
      <span className="flex items-center gap-2">
        <span>{t.nextStep}</span>
        <ArrowRight className="h-4 w-4" />
      </span>
    </Button>
  </div>
);
